/**
 * Centralized Route Configuration
 *
 * Single source of truth for all application routes.
 * Uses React Router v6 with lazy loading for code splitting.
 */

import { lazy } from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';
import type { ProjectView } from '../features/projects/core/components/ViewToggle';

// Lazy load route components for code splitting
const AccessDeniedPage = lazy(() =>
  import('../auth/AccessDeniedPage').then((m) => ({ default: m.AccessDeniedPage }))
);
// Phase 2: Migrated ProjectList and ProjectCreate to features/projects/core
const ProjectList = lazy(() => import('../features/projects/core/components/ProjectList'));
const ProjectCreate = lazy(() => import('../features/projects/core/components/ProjectCreate'));
// Phase 6: Migrated ProjectDetail → ProjectWorkspace (composing all sub-features)
const ProjectWorkspace = lazy(() => import('../features/projects/core/components/ProjectWorkspace'));
// Settings
const SettingsPage = lazy(() => import('../features/settings/pages/SettingsPage'));
// Admin
const AdminUsersPage = lazy(() => import('../features/admin/pages/AdminUsersPage').then((m) => ({ default: m.AdminUsersPage })));
// Dashboard (global cross-project stub)
const DashboardPage = lazy(() => import('../features/projects/dashboard/pages/DashboardPage'));
// Per-project dashboard
const ProjectDashboardPage = lazy(() => import('../features/projects/dashboard/components/ProjectDashboardPage'));
// Reports
const CrossProjectCompliancePage = lazy(() => import('../features/reports/compliance/components/CrossProjectCompliancePage'));
// Pipeline Logs
const ProjectPipelineLogsPage = lazy(() => import('../features/admin/pipeline-logs/pages/ProjectPipelineLogsPage'));
const AdminPipelineLogsPage = lazy(() => import('../features/admin/pipeline-logs/pages/AdminPipelineLogsPage'));

/**
 * Project view types for view switching
 * Re-exported from ViewToggle component
 * Used with query parameters (e.g., ?view=executive)
 */
export type { ProjectView };

/**
 * Application routes configuration
 * Organized hierarchically by feature
 */
export const routes: RouteObject[] = [
  {
    path: '/access-denied',
    element: <AccessDeniedPage />,
  },
  {
    path: '/admin/users',
    element: <AdminUsersPage />,
  },
  {
    path: '/admin/pipeline-logs',
    element: <AdminPipelineLogsPage />,
  },
  {
    path: '/',
    element: <Navigate to="/projects" replace />,
  },
  {
    path: '/dashboard',
    element: <DashboardPage />,
  },
  {
    path: '/settings',
    element: <SettingsPage />,
  },
  {
    path: '/reports',
    children: [
      {
        path: 'compliance',
        element: <CrossProjectCompliancePage />,
      },
    ],
  },
  {
    path: '/projects',
    children: [
      {
        index: true,
        element: <ProjectList />,
      },
      {
        path: 'new',
        element: <ProjectCreate />,
      },
      {
        path: ':id',
        element: <ProjectWorkspace />,
      },
      {
        path: ':id/dashboard',
        element: <ProjectDashboardPage />,
      },
      {
        path: ':id/pipeline-logs',
        element: <ProjectPipelineLogsPage />,
      },
    ],
  },
];

/**
 * Route path helpers
 * Provides type-safe path generation functions
 *
 * @example
 * navigate(routePaths.projectWorkspace('123', 'compliance'))
 * // Result: /projects/123?view=compliance
 */
export const routePaths = {
  /**
   * Home page (project list)
   */
  home: () => '/projects',

  /**
   * Project list page
   */
  projectList: () => '/projects',

  /**
   * Dashboard page
   */
  dashboard: () => '/dashboard',

  /**
   * Create new project page
   */
  projectCreate: () => '/projects/new',

  /**
   * Project workspace with optional view selection
   * @param id - Project ID
   * @param view - Optional view to display (executive, strategic, compliance, sharepoint, report-config)
   */
  projectWorkspace: (id: string, view?: ProjectView) =>
    `/projects/${id}${view ? `?view=${view}` : ''}`,

  /**
   * Per-project dashboard
   * @param id - Project ID
   */
  projectDashboard: (id: string) => `/projects/${id}/dashboard`,

  /**
   * Per-project pipeline logs
   * @param id - Project ID
   */
  projectPipelineLogs: (id: string) => `/projects/${id}/pipeline-logs`,

  /**
   * Organization settings page
   */
  settings: () => '/settings',
} as const;
